import React, { useState, useEffect, useMemo, useRef } from 'react';
import { array, bool, func, number, object, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import classNames from 'classnames';
import {
  ensureListing,
  findApplicableDiscount,
  getDayBasedPricing,
  getDateBasedPricing,
} from '../../../util/data';
import { FormattedMessage, intlShape, injectIntl } from '../../../util/reactIntl';
import {
  required,
  bookingDatesRequired,
  composeValidators,
  numberAtLeast,
  validateAddonsQuantity,
} from '../../../util/validators';
import {
  START_DATE,
  END_DATE,
  getStartOf,
  addTime,
  isSameDay,
  isDateSameOrAfter,
  isInRange,
  timeOfDayFromLocalToTimeZone,
  timeOfDayFromTimeZoneToLocal,
  monthIdString,
  initialVisibleMonth,
  daysBetween,
} from '../../../util/dates';
import { LINE_ITEM_DAY, LINE_ITEM_NIGHT, TIME_SLOT_TIME, propTypes } from '../../../util/types';
import { BOOKING_PROCESS_NAME } from '../../../transactions/transaction';
import {
  Form,
  IconArrowHead,
  PrimaryButton,
  FieldDateRangePicker,
  H6,
  FieldTextInput,
  FieldSelect,
  BlueButton,
} from '../../../components';
import EstimatedCustomerBreakdownMaybe from '../EstimatedCustomerBreakdownMaybe';
import Addons, { AddonsWithQuantity } from './Addons';
import arrayMutators from 'final-form-arrays';
import css from './BookingDatesForm.module.css';

const TODAY = new Date();
const MIN_SEATS_NUMBER = 1;

const nextMonthFn = (currentMoment, timeZone, offset = 1) =>
  getStartOf(currentMoment, 'month', timeZone, offset, 'months');
const prevMonthFn = (currentMoment, timeZone, offset = 1) =>
  getStartOf(currentMoment, 'month', timeZone, -1 * offset, 'months');
const endOfRange = (date, dayCountAvailableForBooking, timeZone) =>
  getStartOf(date, 'day', timeZone, dayCountAvailableForBooking - 1, 'days');

const timeSlotsContain = (timeSlots, date, timeZone) => {
  const foundIndex = timeSlots.findIndex(slot =>
    isInRange(date, slot.attributes.start, slot.attributes.end, 'hour', timeZone)
  );
  return foundIndex > -1;
};

const pickMonthlyTimeSlots = (monthlyTimeSlots, date, timeZone) => {
  const monthId = monthIdString(date, timeZone);
  return monthlyTimeSlots?.[monthId]?.timeSlots || [];
};

const firstBlockedBetween = (monthlyTimeSlots, startDate, endDate, timeZone) => {
  const firstDate = getStartOf(startDate, 'day', timeZone, 1, 'days');
  // Nothing lies between the dates once the next day has reached (or passed)
  // the end. Without the second check a single-day range (start === end) walks
  // forward past the end date until it runs out of fetched time slots and
  // reports a phantom blocked day, which made the picker discard the range.
  if (isSameDay(firstDate, endDate, timeZone) || !isDateSameOrAfter(endDate, firstDate)) {
    return null;
  }
  const timeSlots = pickMonthlyTimeSlots(monthlyTimeSlots, firstDate, timeZone);
  const contains = timeSlotsContain(timeSlots, firstDate, timeZone);
  return contains ? firstBlockedBetween(monthlyTimeSlots, firstDate, endDate, timeZone) : firstDate;
};

const lastBlockedBetween = (monthlyTimeSlots, startDate, endDate, timeZone) => {
  const previousDate = getStartOf(endDate, 'day', timeZone, -1, 'days');
  // Same guard as firstBlockedBetween, walking backwards.
  if (isSameDay(previousDate, startDate, timeZone) || !isDateSameOrAfter(previousDate, startDate)) {
    return null;
  }
  const timeSlots = pickMonthlyTimeSlots(monthlyTimeSlots, previousDate, timeZone);
  return timeSlotsContain(timeSlots, previousDate, timeZone)
    ? lastBlockedBetween(monthlyTimeSlots, startDate, previousDate, timeZone)
    : previousDate;
};

// Exported for tests.
export const isBlockedBetween = (monthlyTimeSlots, timeZone) => (startDate, endDate) => {
  const startInListingTZ = getStartOf(
    timeOfDayFromLocalToTimeZone(startDate, timeZone),
    'day',
    timeZone
  );
  const endInListingTZ = getStartOf(
    timeOfDayFromLocalToTimeZone(endDate, timeZone),
    'day',
    timeZone
  );
  return !!firstBlockedBetween(monthlyTimeSlots, startInListingTZ, endInListingTZ, timeZone);
};

const isStartDateSelected = (timeSlots, startDate, endDate, focusedInput) => {
  return (
    timeSlots && startDate && (!endDate || focusedInput === END_DATE) && focusedInput !== START_DATE
  );
};

const endDateToPickerDate = (unitType, endDate, timeZone) => {
  const isValid = endDate instanceof Date;
  const isDaily = unitType === LINE_ITEM_DAY;
  if (!isValid) {
    return null;
  } else if (isDaily) {
    return getStartOf(endDate, 'day', timeZone, -1, 'days');
  } else {
    return endDate;
  }
};

// Exported for tests.
export const isOutsideRangeFn = (
  monthlyTimeSlots,
  startDate,
  endDate,
  lineItemUnitType,
  dayCountAvailableForBooking,
  timeZone
) => focusedInput => {
  const endOfAvailableRange = dayCountAvailableForBooking - 1;
  const endOfAvailableRangeDate = getStartOf(TODAY, 'day', timeZone, endOfAvailableRange, 'days');
  const outOfBookableDate = getStartOf(TODAY, 'day', timeZone, endOfAvailableRange + 1, 'days');
  const startOfStartDay = getStartOf(startDate, 'day', timeZone);
  const startDateSelected = isStartDateSelected(monthlyTimeSlots, startDate, endDate, focusedInput);
  const endOfBookableRange = startDateSelected
    ? firstBlockedBetween(monthlyTimeSlots, startDate, outOfBookableDate, timeZone)
    : null;
  if (endOfBookableRange) {
    return day => {
      const timeOfDay = timeOfDayFromLocalToTimeZone(day, timeZone);
      const dayInListingTZ = getStartOf(timeOfDay, 'day', timeZone);
      const lastDayToEndBooking = endDateToPickerDate(
        lineItemUnitType,
        endOfBookableRange,
        timeZone
      );
      return (
        !isDateSameOrAfter(dayInListingTZ, startOfStartDay) ||
        !isDateSameOrAfter(lastDayToEndBooking, dayInListingTZ)
      );
    };
  }
  const endDateSelected = monthlyTimeSlots && endDate && !startDate && focusedInput !== END_DATE;
  const previousBookedDate = endDateSelected
    ? lastBlockedBetween(monthlyTimeSlots, TODAY, endDate, timeZone)
    : null;
  if (previousBookedDate) {
    return day => {
      const timeOfDay = timeOfDayFromLocalToTimeZone(day, timeZone);
      const dayInListingTZ = getStartOf(timeOfDay, 'day', timeZone);
      const firstDayToStartBooking = getStartOf(previousBookedDate, 'day', timeZone, 1, 'days');
      return (
        !isDateSameOrAfter(dayInListingTZ, firstDayToStartBooking) ||
        !isDateSameOrAfter(endOfAvailableRangeDate, dayInListingTZ)
      );
    };
  }
  return day => {
    const timeOfDay = timeOfDayFromLocalToTimeZone(day, timeZone);
    const dayInListingTZ = getStartOf(timeOfDay, 'day', timeZone);
    // Compare against midnight, not the current moment: a day is compared as
    // its 00:00 timestamp, so comparing to "now" pushed today out of range
    // and killed same-day bookings.
    const startOfToday = getStartOf(TODAY, 'day', timeZone);
    return (
      !isDateSameOrAfter(dayInListingTZ, startOfToday) ||
      !isDateSameOrAfter(endOfAvailableRangeDate, dayInListingTZ)
    );
  };
};

const timeSlotEqualsDay = (timeSlot, day, timeZone) => {
  const isTimeBased = timeSlot?.attributes?.type === TIME_SLOT_TIME;
  return isTimeBased
    ? isInRange(day, timeSlot.attributes.start, timeSlot.attributes.end, undefined, timeZone)
    : false;
};

// Exported for tests.
export const isDayBlockedFn = (
  monthlyTimeSlots,
  startDate,
  endDate,
  unitType,
  dayCountAvailableForBooking,
  timeZone
) => focusedInput => {
  const endOfAvailableRange = dayCountAvailableForBooking - 1;
  const outOfBookableDate = getStartOf(TODAY, 'day', timeZone, endOfAvailableRange + 1, 'days');
  const timeSlotsOnSelectedMonth = pickMonthlyTimeSlots(monthlyTimeSlots, startDate, timeZone);
  const startDateSelected = isStartDateSelected(
    timeSlotsOnSelectedMonth,
    startDate,
    endDate,
    focusedInput
  );
  const endOfBookableRange = startDateSelected
    ? firstBlockedBetween(monthlyTimeSlots, startDate, outOfBookableDate, timeZone)
    : null;
  const selectingEndDate =
    timeSlotsOnSelectedMonth &&
    !startDate &&
    !endDate &&
    focusedInput === END_DATE &&
    unitType === LINE_ITEM_NIGHT;
  if (selectingEndDate) {
    return day => {
      const timeOfDay = timeOfDayFromLocalToTimeZone(day, timeZone);
      const dayInListingTZ = getStartOf(timeOfDay, 'day', timeZone);
      const timeSlots = pickMonthlyTimeSlots(monthlyTimeSlots, dayInListingTZ, timeZone);
      return !timeSlots.find(timeSlot => timeSlotEqualsDay(timeSlot, dayInListingTZ));
    };
  } else if (endOfBookableRange || !timeSlotsOnSelectedMonth) {
    return () => false;
  } else {
    return day => {
      const timeOfDay = timeOfDayFromLocalToTimeZone(day, timeZone);
      const dayInListingTZ = getStartOf(timeOfDay, 'day', timeZone);
      const timeSlots = pickMonthlyTimeSlots(monthlyTimeSlots, dayInListingTZ, timeZone);
      return !timeSlots.find(timeSlot => timeSlotEqualsDay(timeSlot, dayInListingTZ));
    };
  }
};

const fetchMonthData = (
  date,
  listingId,
  dayCountAvailableForBooking,
  timeZone,
  onFetchTimeSlots
) => {
  const endOfRangeDate = endOfRange(TODAY, dayCountAvailableForBooking, timeZone);
  const startOfToday = getStartOf(TODAY, 'day', timeZone);
  if (isInRange(date, startOfToday, endOfRangeDate)) {
    // Query from midnight, not "now": the availability API clips a returned
    // time slot to the queried start, and a slot that begins mid-day makes
    // today look blocked in the calendar.
    const start = isDateSameOrAfter(startOfToday, date) ? startOfToday : date;
    const nextMonthDate = nextMonthFn(date, timeZone);
    const end = isDateSameOrAfter(nextMonthDate, endOfRangeDate)
      ? getStartOf(endOfRangeDate, 'day', timeZone)
      : nextMonthDate;
    onFetchTimeSlots(listingId, start, end, timeZone);
  }
};

const handleMonthClick = (
  currentMonth,
  monthlyTimeSlots,
  fetchMonthData,
  dayCountAvailableForBooking,
  timeZone,
  listingId,
  onFetchTimeSlots
) => monthFn => {
  fetchMonthData(
    monthFn(currentMonth, timeZone, 2),
    listingId,
    dayCountAvailableForBooking,
    timeZone,
    onFetchTimeSlots
  );
  const monthId = monthIdString(currentMonth, timeZone);
  const currentMonthData = monthlyTimeSlots[monthId];
  if (currentMonthData && currentMonthData.fetchTimeSlotsError) {
    fetchMonthData(
      currentMonth,
      listingId,
      dayCountAvailableForBooking,
      timeZone,
      onFetchTimeSlots
    );
  }
};

const handleFormSubmit = (setFocusedInput, onSubmit) => e => {
  const { startDate, endDate } = e.bookingDates || {};
  if (!startDate) {
    e.preventDefault();
    setFocusedInput(START_DATE);
  } else if (!endDate) {
    e.preventDefault();
    setFocusedInput(END_DATE);
  } else {
    onSubmit(e);
  }
};

const handleFocusedInputChange = setFocusedInput => focusedInput => {
  setFocusedInput(focusedInput);
};

const handleFormSpyChange = (
  listingId,
  isOwnListing,
  fetchLineItemsInProgress,
  onFetchTransactionLineItems,
  addonsFromPublicData,
  discountsFromPublicData,
  dayPricingsFromPublicData,
  datePricingsFromPublicData,
  price,
  config,
  charterBoat,
  lastFetchKeyRef
) => formValues => {
  const pickedDates =
    formValues.values && formValues.values.bookingDates ? formValues.values.bookingDates : {};
  const { startDate, endDate: pickedEndDate } = pickedDates;
  // We treat the date picker's end date as the LAST day of the rental
  // (inclusive). Sharetribe's daily booking model wants an exclusive end,
  // so we bump it by one day before passing anything downstream — day
  // counts, pricing helpers, and the orderData posted to the server.
  const endDate = pickedEndDate ? addTime(pickedEndDate, 1, 'days') : null;
  const bookingDates = { startDate, endDate };

  const formAddons = formValues?.values?.addons;
  const addons = formAddons
    ? formAddons?.map(addonKey => {
        const addonConfig = addonsFromPublicData.find(a => a.key === addonKey);
        const addonQuantity = formValues?.values?.addonsQuantity?.[addonKey] || 1;
        return {
          ...addonConfig,
          quantity: addonQuantity,
        };
      })
    : [];
  const addonsMaybe = addons ? { addons } : {};
  const datesDayDifference = startDate && endDate ? daysBetween(startDate, endDate) : null;
  const durationDiscount = findApplicableDiscount(discountsFromPublicData, datesDayDifference);
  const durationDiscountMaybe = durationDiscount ? { durationDiscount } : {};
  const seats = formValues?.values?.seats ? Number(formValues.values.seats) : null;
  const seatsMaybe = seats ? { seats } : {};
  const hasBookingDates = charterBoat ? startDate && endDate && seats : startDate && endDate;
  const shouldFetchDayBasedPricing = hasBookingDates && dayPricingsFromPublicData;
  const dayBasedPricing =
    shouldFetchDayBasedPricing &&
    getDayBasedPricing(bookingDates, dayPricingsFromPublicData, price, config);
  const dayBasedPricingMaybe = dayBasedPricing ? { dayBasedPricing } : {};
  const shouldFetchDateBasedPricing = hasBookingDates && datePricingsFromPublicData;
  const dateBasedPricing =
    shouldFetchDateBasedPricing &&
    getDateBasedPricing(bookingDates, datePricingsFromPublicData, price, config);
  const dateBasedPricingMaybe = dateBasedPricing ? { dateBasedPricing } : {};
  if (startDate && endDate && !fetchLineItemsInProgress) {
    const orderData = {
      ...addonsMaybe,
      ...dayBasedPricingMaybe,
      ...dateBasedPricingMaybe,
      ...durationDiscountMaybe,
      ...seatsMaybe,
      bookingStart: startDate,
      bookingEnd: endDate,
      discountCode: formValues?.values?.discountCode,
    };
    // Skip refetch when the pricing-relevant inputs haven't changed.
    // Without this, typing in fields like rentalLocation re-fires
    // FormSpy onChange and refetches line items on every keystroke.
    const fetchKey = JSON.stringify({
      orderData,
      listingId: listingId?.uuid || listingId,
      isOwnListing,
    });
    if (lastFetchKeyRef && lastFetchKeyRef.current === fetchKey) {
      return;
    }
    if (lastFetchKeyRef) {
      lastFetchKeyRef.current = fetchKey;
    }
    onFetchTransactionLineItems({
      orderData,
      listingId,
      isOwnListing,
    });
  }
};

const PrevIcon = props => (
  <IconArrowHead {...props} direction="left" rootClassName={css.arrowIcon} />
);
const NextIcon = props => (
  <IconArrowHead {...props} direction="right" rootClassName={css.arrowIcon} />
);

const Next = props => {
  const { currentMonth, dayCountAvailableForBooking, timeZone } = props;
  const nextMonthDate = nextMonthFn(currentMonth, timeZone);
  return isDateSameOrAfter(
    nextMonthDate,
    endOfRange(TODAY, dayCountAvailableForBooking, timeZone)
  ) ? null : (
    <NextIcon />
  );
};
const Prev = props => {
  const { currentMonth, timeZone } = props;
  const prevMonthDate = prevMonthFn(currentMonth, timeZone);
  const currentMonthDate = getStartOf(TODAY, 'month', timeZone);
  return isDateSameOrAfter(prevMonthDate, currentMonthDate) ? <PrevIcon /> : null;
};

export const BookingDatesFormComponent = props => {
  const [focusedInput, setFocusedInput] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(getStartOf(TODAY, 'month', props.timeZone));
  const [discountCode, setDiscountCode] = useState('');
  // Local state to toggle the reserve section and confirm reservation
  const [reserveMode, setReserveMode] = useState(false);
  const [reservationConfirmed, setReservationConfirmed] = useState(false);

  useEffect(() => {
    if (props.onMonthChanged) {
      props.onMonthChanged(monthId);
    }
  }, [currentMonth]);

  const {
    rootClassName,
    className,
    price: unitPrice,
    listingId,
    isOwnListing,
    fetchLineItemsInProgress,
    onFetchTransactionLineItems,
    onSubmit,
    timeZone,
    dayCountAvailableForBooking,
    marketplaceName,
    payoutDetailsWarning,
    publicData,
    addonsFromPublicData,
    discountsFromPublicData,
    dayPricingsFromPublicData,
    datePricingsFromPublicData,
    charterBoat,
    charterBoatSeats,
    config,
    ...rest
  } = props;

  const codes = Array.isArray(props.codes)
    ? [
        ...props.codes,
        { code: 'Ottawa10', percentage: '10' },
        { code: 'Paradise10', percentage: '10' },
        { code: 'Ny1sKjvp', percentage: '20' },
      ]
    : [
        { code: 'Ottawa10', percentage: '10' },
        { code: 'Paradise10', percentage: '10' },
        { code: 'Ny1sKjvp', percentage: '20' },
      ];
  const rentalLocation = props.rentalLocation;
  const classes = classNames(rootClassName || css.root, className);
  const onFormSubmit = handleFormSubmit(setFocusedInput, onSubmit);
  const onFocusedInputChange = handleFocusedInputChange(setFocusedInput);
  let selectedCode = '';

  const lastFetchKeyRef = useRef(null);
  const onFormSpyChange = handleFormSpyChange(
    listingId,
    isOwnListing,
    fetchLineItemsInProgress,
    onFetchTransactionLineItems,
    addonsFromPublicData,
    discountsFromPublicData,
    dayPricingsFromPublicData,
    datePricingsFromPublicData,
    unitPrice,
    config,
    charterBoat,
    lastFetchKeyRef
  );

  const validateCode = value => {
    const codeExists = codes.some(code => code.code === value);
    let selectedCode = '';
    if (codeExists) {
      const code = codes.find(code => code.code === value);
      selectedCode = code.percentage;
      setDiscountCode(value);
      console.log(selectedCode);
    }
  };

  // Simple required validator for the reserve email field.
  const requiredEmail = value => (value ? undefined : 'Email is required');

  const radius = { borderRadius: '30px' };

  // Pre-fill bookingDates from URL query params (?bookingStart=YYYY-MM-DD&
  // bookingEnd=YYYY-MM-DD). Used by the date-change rebook redirect so the
  // renter lands on the listing with the agreed-upon new dates already
  // selected. Also opens the order modal on mobile.
  //
  // CRITICAL: this MUST be memoized. Without useMemo, every render
  // returns a new `{startDate, endDate}` object with fresh Date
  // instances. FinalForm uses reference equality on initialValues and
  // would re-initialize the form on every render — that re-runs the
  // FormSpy onChange, which fires `fetchTransactionLineItems`, which
  // re-renders, which produces a new initialValues, looping forever.
  const isFromDateChange = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('fromDateChange') === '1';
  }, []);
  const initialBookingDates = useMemo(() => {
    if (typeof window === 'undefined') return null;
    const queryParams = new URLSearchParams(window.location.search);
    const startStr = queryParams.get('bookingStart');
    const endStr = queryParams.get('bookingEnd');
    if (!startStr || !endStr) return null;
    const parseLocalDate = s => {
      const [y, m, d] = s.split('-').map(Number);
      if (!y || !m || !d) return null;
      const dt = getStartOf(new Date(y, m - 1, d), 'day', timeZone);
      return isNaN(dt) ? null : dt;
    };
    const startDate = parseLocalDate(startStr);
    const endDate = parseLocalDate(endStr);
    if (!startDate || !endDate || startDate >= endDate) return null;
    return { startDate, endDate };
  }, [timeZone]);

  return (
    <FinalForm
      {...rest}
      mutators={{ ...arrayMutators }}
      unitPrice={unitPrice}
      onSubmit={onFormSubmit}
      initialValues={{
        seats: charterBoatSeats ? MIN_SEATS_NUMBER : undefined,
        reserveEmail: '',
        ...(initialBookingDates ? { bookingDates: initialBookingDates } : {}),
      }}
      render={fieldRenderProps => {
        const {
          endDatePlaceholder,
          startDatePlaceholder,
          formId,
          handleSubmit,
          intl,
          lineItemUnitType,
          values,
          monthlyTimeSlots,
          lineItems,
          fetchLineItemsError,
          onFetchTimeSlots,
          form,
        } = fieldRenderProps;
        const { startDate, endDate } =
          (values && values.bookingDates ? values.bookingDates : {}) || {};

        // Host-defined booking duration limits.
        const minBookingDays = Number(publicData?.minBookingDays) || null;
        const maxBookingDays = Number(publicData?.maxBookingDays) || null;
        // The picker's end date is the LAST rental day (inclusive). Add 1
        // to bridge to the exclusive-end convention daysBetween uses, so
        // Thu→Sat reads as 3 days here just like in the breakdown.
        const selectedDays =
          startDate && endDate ? daysBetween(startDate, endDate) + 1 : null;
        const tooFewDays =
          selectedDays != null && minBookingDays && selectedDays < minBookingDays;
        const tooManyDays =
          selectedDays != null && maxBookingDays && selectedDays > maxBookingDays;
        const durationError = tooFewDays
          ? `This listing requires a minimum of ${minBookingDays} ${minBookingDays === 1 ? 'day' : 'days'}.`
          : tooManyDays
          ? `This listing has a maximum of ${maxBookingDays} ${maxBookingDays === 1 ? 'day' : 'days'}.`
          : null;

        const startDateErrorMessage = intl.formatMessage({
          id: 'FieldDateRangeInput.invalidStartDate',
        });
        const endDateErrorMessage = intl.formatMessage({
          id: 'FieldDateRangeInput.invalidEndDate',
        });
        // Pass the exclusive end to the breakdown so the displayed booking
        // period subtracts back to the user's picked last day. Matches the
        // exclusive-end convention used everywhere else in the booking path.
        const breakdownData =
          startDate && endDate
            ? {
                startDate,
                endDate: addTime(endDate, 1, 'days'),
              }
            : null;
        const showEstimatedBreakdown =
          breakdownData && lineItems && !fetchLineItemsInProgress && !fetchLineItemsError;
        const dateFormatOptions = {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        };
        const startOfToday = getStartOf(TODAY, 'day', timeZone);
        const tomorrow = addTime(startOfToday, 1, 'days');
        const startDatePlaceholderText =
          startDatePlaceholder || intl.formatDate(startOfToday, dateFormatOptions);
        const endDatePlaceholderText =
          endDatePlaceholder || intl.formatDate(tomorrow, dateFormatOptions);

        const onMonthClick = handleMonthClick(
          currentMonth,
          monthlyTimeSlots,
          fetchMonthData,
          dayCountAvailableForBooking,
          timeZone,
          listingId,
          onFetchTimeSlots
        );
        const isDayBlocked = isDayBlockedFn(
          monthlyTimeSlots,
          startDate,
          endDate,
          lineItemUnitType,
          dayCountAvailableForBooking,
          timeZone
        );
        const isOutsideRange = isOutsideRangeFn(
          monthlyTimeSlots,
          startDate,
          endDate,
          lineItemUnitType,
          dayCountAvailableForBooking,
          timeZone
        );
        const showAddons = addonsFromPublicData && addonsFromPublicData.length > 0;
        const seats = charterBoatSeats || MIN_SEATS_NUMBER;
        const hasFormSeats = values.seats && typeof values.seats !== 'undefined';
        const formSeats = hasFormSeats ? values.seats : null;
        const showAddonsWithQuantity = charterBoat && formSeats > 0;
        const seatsMessage = intl.formatMessage({
          id: 'BookingDatesForm.seats',
        });
        const seatsPlaceholderMessage = intl.formatMessage(
          {
            id: 'BookingDatesForm.seatsPlaceholder',
          },
          { count: MIN_SEATS_NUMBER }
        );
        const seatsRequiredMessage = intl.formatMessage({
          id: 'BookingDatesForm.seatsRequired',
        });
        const charterBoatSeatsNumber = charterBoat ? Number(charterBoatSeats) : null;
        const hasCharterBoatSeats = charterBoatSeatsNumber > 0;
        const seatsArray =
          charterBoatSeatsNumber && hasCharterBoatSeats
            ? [...Array(Number(charterBoatSeatsNumber)).keys()].map(i => i + 1)
            : [];
        const seatsValidators = numberAtLeast(seatsRequiredMessage, 1);
        const addonsQuantityError = addonsFromPublicData?.some(addon => {
          const isChecked = values?.addons?.includes(addon.key);
          const isQuantityAboveMaximum =
            values?.addonsQuantity?.[addon.key] > formSeats ||
            values?.addonsQuantity?.[addon.key] > seats;
          const isQuantityBelowMinimum = values?.addonsQuantity?.[addon.key] < MIN_SEATS_NUMBER;
          return isChecked && (isQuantityAboveMaximum || isQuantityBelowMinimum);
        });

        // Define the reserve submission handler inside the render callback so that "values" is in scope.
        const handleReserveSubmitInternal = async () => {
          const reserveEmailValue = values.reserveEmail;
          if (!reserveEmailValue) {
            alert('Please enter your email.');
            return;
          }
          // Construct the payload with required fields.
          const payload = {
            customerId: 'WetRentals',
            email: reserveEmailValue,
            inventoryId: listingId,
            locationId: 'WetRentals',
          };
          try {
            const response = await fetch('https://v2.api.rentaltide.com/holdAsset', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            });
            if (!response.ok) {
              throw new Error('API call failed');
            }
            const data = await response.json();
            console.log('Waitlist entry created:', data);
            setReserveMode(false);
            setReservationConfirmed(true);
            form.change('reserveEmail', '');
          } catch (error) {
            console.error('Error reserving boat:', error);
            alert('There was an error processing your reservation. Please try again.');
          }
        };

        return (
          <Form onSubmit={handleSubmit} className={classes} enforcePagePreloadFor="CheckoutPage">
            <FormSpy subscription={{ values: true }} onChange={onFormSpyChange} />
            {isFromDateChange ? (
              <div className={css.dateChangeBanner}>
                <strong className={css.dateChangeBannerTitle}>
                  Date change approved
                </strong>
                <p className={css.dateChangeBannerBody}>
                  Your original booking has been cancelled and refunded. The new
                  dates are pre-filled below — confirm payment to lock them in.
                </p>
              </div>
            ) : null}
            <FieldDateRangePicker
              theme="light"
              className={css.bookingDates}
              popupClassName={css.bookingDatesPopup}
              name="bookingDates"
              isDaily={lineItemUnitType === LINE_ITEM_DAY}
              startDateId={`${formId}.bookingStartDate`}
              startDateLabel={intl.formatMessage({
                id: 'BookingDatesForm.bookingStartTitle',
              })}
              startDatePlaceholderText={startDatePlaceholderText}
              endDateId={`${formId}.bookingEndDate`}
              endDateLabel={intl.formatMessage({
                id: 'BookingDatesForm.bookingEndTitle',
              })}
              endDatePlaceholderText={endDatePlaceholderText}
              format={v => {
                const { startDate, endDate } = v || {};
                const formattedStart = startDate
                  ? timeOfDayFromTimeZoneToLocal(startDate, timeZone)
                  : startDate;
                const formattedEnd = endDate
                  ? timeOfDayFromTimeZoneToLocal(endDate, timeZone)
                  : endDate;
                return v ? { startDate: formattedStart, endDate: formattedEnd } : v;
              }}
              parse={v => {
                const { startDate, endDate } = v || {};
                const parsedStart = startDate
                  ? getStartOf(timeOfDayFromLocalToTimeZone(startDate, timeZone), 'day', timeZone)
                  : startDate;
                const parsedEnd = endDate
                  ? getStartOf(timeOfDayFromLocalToTimeZone(endDate, timeZone), 'day', timeZone)
                  : endDate;
                return v ? { startDate: parsedStart, endDate: parsedEnd } : v;
              }}
              useMobileMargins
              validate={composeValidators(
                required(
                  intl.formatMessage({
                    id: 'BookingDatesForm.requiredDate',
                  })
                ),
                bookingDatesRequired(startDateErrorMessage, endDateErrorMessage)
              )}
              isDayBlocked={isDayBlocked(END_DATE)}
              isOutsideRange={isOutsideRange(END_DATE)}
              isBlockedBetween={range =>
                isBlockedBetween(monthlyTimeSlots, timeZone)(range[0], range[1])
              }
              disabled={fetchLineItemsInProgress}
              showPreviousMonthStepper={isDateSameOrAfter(
                prevMonthFn(currentMonth, timeZone),
                getStartOf(TODAY, 'month', timeZone)
              )}
              showNextMonthStepper={
                !isDateSameOrAfter(
                  nextMonthFn(currentMonth, timeZone),
                  endOfRange(TODAY, dayCountAvailableForBooking, timeZone)
                )
              }
              onMonthChange={date => {
                const localizedDate = timeOfDayFromLocalToTimeZone(date, timeZone);
                onMonthClick(localizedDate < currentMonth ? prevMonthFn : nextMonthFn);
                setCurrentMonth(localizedDate);
              }}
            />
            {durationError ? (
              <p className={classNames(css.durationMessage, css.durationMessageError)}>
                {durationError}
              </p>
            ) : minBookingDays || maxBookingDays ? (
              <p className={css.durationMessage}>
                {minBookingDays && maxBookingDays
                  ? `Rental duration: ${minBookingDays}–${maxBookingDays} days.`
                  : minBookingDays
                  ? `Minimum rental: ${minBookingDays} ${minBookingDays === 1 ? 'day' : 'days'}.`
                  : `Maximum rental: ${maxBookingDays} ${maxBookingDays === 1 ? 'day' : 'days'}.`}
              </p>
            ) : null}
            {codes && (
              <FieldTextInput
                id="discount"
                name="discountCode"
                label="Discount Code"
                placeholder="Input discount code here"
                validate={validateCode}
                type="text"
                className={`${
                  codes?.some(code => code?.code === values?.discountCode)
                    ? css.validCode
                    : css.invalidCode
                } ${css.discountCode}`}
              />
            )}
            {rentalLocation && (
              <FieldTextInput
                id="rentalLocation"
                name="rentalLocation"
                label="Where Would You Like To Ride?"
                placeholder="Input desired rental location here"
                validate={validateCode}
                required={true}
                type="text"
                className={css.rentalLocation}
              />
            )}
            {charterBoat && (
              <FieldSelect
                id="seats"
                name="seats"
                className={css.seats}
                label={seatsMessage}
                disabled={!seatsArray.length}
                validate={seatsValidators}
              >
                {seatsArray.map(s => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </FieldSelect>
            )}
            {showAddons &&
              (showAddonsWithQuantity ? (
                <AddonsWithQuantity
                  addonsFromPublicData={addonsFromPublicData}
                  values={values}
                  formSeats={formSeats}
                  seats={seats}
                  form={form}
                  addonsQuantityError={addonsQuantityError}
                />
              ) : (
                <Addons addonsFromPublicData={addonsFromPublicData} />
              ))}
            {showEstimatedBreakdown ? (
              <div className={css.priceBreakdownContainer}>
                <H6 as="h3" className={css.bookingBreakdownTitle}>
                  <FormattedMessage id="BookingDatesForm.priceBreakdownTitle" />
                </H6>
                <hr className={css.totalDivider} />
                <EstimatedCustomerBreakdownMaybe
                  breakdownData={breakdownData}
                  lineItems={lineItems}
                  discountCode={values?.discountCode}
                  discountPercent={selectedCode}
                  rentalLocation={values?.rentalLocation}
                  timeZone={timeZone}
                  currency={unitPrice.currency}
                  marketplaceName={marketplaceName}
                  processName={BOOKING_PROCESS_NAME}
                  dayBasedPricing={dayPricingsFromPublicData}
                  dateBasedPricing={datePricingsFromPublicData}
                />
              </div>
            ) : null}
            {fetchLineItemsError ? (
              <span className={css.sideBarError}>
                <FormattedMessage id="BookingDatesForm.fetchLineItemsError" />
              </span>
            ) : null}

            <div className={css.submitButton}>
              <PrimaryButton
                type="submit"
                inProgress={fetchLineItemsInProgress}
                disabled={addonsQuantityError || !!durationError}
              >
                {publicData?.instantBook ? (
                  <FormattedMessage
                    id="BookingDatesForm.instantBook"
                    defaultMessage="Instant Book"
                  />
                ) : (
                  <FormattedMessage id="BookingDatesForm.requestToBook" />
                )}
              </PrimaryButton>
            </div>
            <p className={css.finePrint}>
              {payoutDetailsWarning ? (
                payoutDetailsWarning
              ) : (
                <FormattedMessage
                  id={
                    isOwnListing
                      ? 'BookingDatesForm.ownListing'
                      : 'BookingDatesForm.youWontBeChargedInfo'
                  }
                />
              )}
            </p>

      
          </Form>
        );
      }}
    />
  );
};

BookingDatesFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  price: null,
  isOwnListing: false,
  startDatePlaceholder: null,
  endDatePlaceholder: null,
  lineItems: null,
  fetchLineItemsError: null,
  monthlyTimeSlots: null,
};

BookingDatesFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  marketplaceName: string.isRequired,
  lineItemUnitType: propTypes.lineItemUnitType.isRequired,
  price: propTypes.money,
  isOwnListing: bool,
  monthlyTimeSlots: object,
  onFetchTimeSlots: func.isRequired,
  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  intl: intlShape.isRequired,
  startDatePlaceholder: string,
  endDatePlaceholder: string,
  dayCountAvailableForBooking: number.isRequired,
};

const BookingDatesForm = compose(injectIntl)(BookingDatesFormComponent);
BookingDatesForm.displayName = 'BookingDatesForm';

export default BookingDatesForm;
